import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { useGamification } from '../context/GamificationContext';
import { toast } from 'sonner';
import { 
  Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Repeat2,
  Play, ChevronLeft, ChevronRight, Volume2, VolumeX,
  BarChart3, Rocket, Flag, Trash2, AtSign, Sparkles, Pencil, Check, X
} from 'lucide-react';
import PostInsightsModal from './PostInsightsModal';
import { ShareIconButton } from './ShareSheet';
import BoostPostModal from './BoostPostModal';
import { PromoteButton } from './PromoteModal';
import ShareToSocialButton from './ShareToSocialButton';
import { API } from '../config/api';
import UserAvatar from './UserAvatar';
import { getProfileUrl, getDisplayHandle } from '../utils/profileUtils';
import { LinkEmbedCard } from './LinkEmbedCard';
import RichText from './RichText';
import LockedOverlay from './LockedOverlay';
import MonetizeMenu from './MonetizeMenu';
import { TipBadge } from './TipBadge';

// Helper function to construct proper media URLs
const getMediaUrl = (url) => {
  if (!url) return '';
  
  // Already a full URL - check if it needs fixing for old deployments
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Check if this is pointing to an old deployment that no longer exists
    const oldDomains = [
      'restore-dmv.emergent.host',
      'dmv-soundstage-',
      '.preview.emergentagent.com'
    ];
    
    // If URL points to old domain, try to extract the path and use current origin
    for (const oldDomain of oldDomains) {
      if (url.includes(oldDomain)) {
        try {
          const urlObj = new URL(url);
          const path = urlObj.pathname;
          // Use current origin for same-domain access
          if (typeof window !== 'undefined') {
            const newUrl = `${window.location.origin}${path}`;
            console.log('[getMediaUrl] Redirecting old URL:', url, '->', newUrl);
            return newUrl;
          }
        } catch (e) {
          console.error('[getMediaUrl] Failed to parse URL:', url);
        }
      }
    }
    return url;
  }
  
  // Get base URL - for same-origin (production), API is empty string
  // For preview, API has the full URL
  const baseUrl = API ? API.replace('/api', '') : '';
  
  // Already has /api prefix
  if (url.startsWith('/api/')) {
    return `${baseUrl}${url}`;
  }
  
  // Starts with /uploads - need to add /api prefix
  if (url.startsWith('/uploads/')) {
    return `${baseUrl}/api${url}`;
  }
  
  // Any other relative URL
  const apiBase = API || '/api';
  return `${apiBase}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Fallback placeholder for broken images
const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23374151" width="400" height="300"/%3E%3Ctext x="200" y="150" fill="%239CA3AF" font-size="14" text-anchor="middle" font-family="system-ui"%3EImage unavailable%3C/text%3E%3Ctext x="200" y="175" fill="%236B7280" font-size="10" text-anchor="middle" font-family="system-ui"%3ETap to retry%3C/text%3E%3C/svg%3E';



export function PostCard({ post, currentUser }) {
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated, user } = useAuth();
  const gamification = useGamification();
  const [liked, setLiked] = useState(post.liked_by_me || false);
  const [likeCount, setLikeCount] = useState(post.likes || 0);
  const [saved, setSaved] = useState(post.saved_by_me || false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [showInsights, setShowInsights] = useState(false);
  const [showBoost, setShowBoost] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || post.content_text || '');
  const [postContent, setPostContent] = useState(post.content || post.content_text || '');
  const [isEdited, setIsEdited] = useState(post.is_edited || false);
  const [reposted, setReposted] = useState(false);
  const [repostCount, setRepostCount] = useState(post.reposts_count || 0);
  const [contentUnlocked, setContentUnlocked] = useState(false);

  const isLocked = post.content_lock && post.content_lock.lock_type && post.content_lock.lock_type !== 'public' && !contentUnlocked;

  const mediaItems = post.media_urls || [];
  const hasMultipleMedia = mediaItems.length > 1;
  
  // Check if current user is the post owner
  const isOwner = currentUser?.user_id === post.user_id || 
                  currentUser?.user_id === post.artist_id ||
                  currentUser?.artist_id === post.artist_id;
  const isAdmin = currentUser?.role === 'owner' || currentUser?.role === 'admin';
  const canViewInsights = isOwner || isAdmin;
  const canBoost = isOwner;

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in to like posts');
      navigate('/login');
      return;
    }

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const endpoint = liked ? 'unlike' : 'like';
      
      const res = await fetch(`${API}/posts/${post.post_id}/${endpoint}`, {
        method: 'POST',
        headers
      });
      
      setLiked(!liked);
      setLikeCount(prev => liked ? prev - 1 : prev + 1);

      if (!liked && res.ok) {
        const data = await res.json();
        if (data.gamification) gamification?.processXpResult({ ...data.gamification, action: 'like_given' });
      }
    } catch (err) {
      toast.error('Failed to like post');
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in to save posts');
      navigate('/login');
      return;
    }

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const endpoint = saved ? 'unsave' : 'save';
      
      await fetch(`${API}/posts/${post.post_id}/${endpoint}`, {
        method: 'POST',
        headers
      });
      
      setSaved(!saved);
      toast.success(saved ? 'Removed from saved' : 'Post saved');
    } catch (err) {
      toast.error('Failed to save post');
    }
  };

  const handleShare = async () => {
    const ogShareUrl = `${window.location.origin}/api/og/post/${post.post_id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Post by @${post.author_handle}`,
          url: ogShareUrl
        });
      } else {
        await navigator.clipboard.writeText(ogShareUrl);
        toast.success('Link copied!');
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.message?.includes('cancel')) {
        return;
      }
      try {
        await navigator.clipboard.writeText(ogShareUrl);
        toast.success('Link copied!');
      } catch {
        console.error('Share failed:', error);
      }
    }
  };

  const handleRepost = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in to repost');
      navigate('/login');
      return;
    }
    try {
      const res = await fetch(`${API}/posts/${post.post_id}/repost`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setReposted(data.reposted);
        setRepostCount(prev => data.reposted ? prev + 1 : prev - 1);
        toast.success(data.reposted ? 'Reposted!' : 'Repost removed');
      }
    } catch {
      toast.error('Failed to repost');
    }
  };

  const handleEditPost = async () => {
    if (!editContent.trim()) return;
    try {
      const res = await fetch(`${API}/posts/${post.post_id}/edit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ content_text: editContent.trim() }),
      });
      if (res.ok) {
        setPostContent(editContent.trim());
        setIsEdited(true);
        setIsEditing(false);
        toast.success('Post updated');
      } else {
        toast.error('Failed to update post');
      }
    } catch { toast.error('Failed to update post'); }
  };

  const nextMedia = () => {
    setCurrentMediaIndex(prev => (prev + 1) % mediaItems.length);
  };

  const prevMedia = () => {
    setCurrentMediaIndex(prev => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  const isVideo = (url) => {
    return url?.match(/\.(mp4|mov|webm)$/i);
  };

  const currentMedia = mediaItems[currentMediaIndex];

  // Determine if current user can comment (listeners can, artists cannot)
  const canComment = currentUser?.role === 'listener' || currentUser?.role === 'owner';

  return (
    <Card className="glass-card border-border overflow-hidden" data-testid={`post-${post.post_id}`}>
      {/* Post Header */}
      <div className="flex items-center justify-between p-4">
        <Link to={getProfileUrl({ handle: post.author_handle, user_id: post.user_id || post.artist_id })} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <UserAvatar
            src={post.author_avatar}
            alt={post.author_name}
            size="md"
            fallbackInitial={post.author_name}
          />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium leading-tight">{post.author_name}</p>
              {post.is_sponsored && (
                <Badge variant="outline" className="text-xs h-5">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Sponsored
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{getDisplayHandle({ handle: post.author_handle, name: post.author_name })}</p>
          </div>
        </Link>
        
        {/* Post Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-muted-foreground">
              <MoreHorizontal className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {/* Owner/Admin Only Options */}
            {canViewInsights && (
              <DropdownMenuItem onClick={() => setShowInsights(true)}>
                <BarChart3 className="w-4 h-4 mr-2" />
                View Insights
              </DropdownMenuItem>
            )}
            {canBoost && !post.is_boosted && (
              <DropdownMenuItem onClick={() => setShowBoost(true)}>
                <Rocket className="w-4 h-4 mr-2" />
                Boost Post
              </DropdownMenuItem>
            )}
            {(isOwner || isAdmin) && (
              <DropdownMenuItem onClick={() => setIsEditing(true)} data-testid="edit-post-btn">
                <Pencil className="w-4 h-4 mr-2" />
                Edit Post
              </DropdownMenuItem>
            )}
            {(canViewInsights || canBoost) && <DropdownMenuSeparator />}
            
            {/* Public Options */}
            <DropdownMenuItem onClick={handleShare}>
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(`/post/${post.post_id}`)}>
              <MessageCircle className="w-4 h-4 mr-2" />
              View Post
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-muted-foreground">
              <Flag className="w-4 h-4 mr-2" />
              Report
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Media Carousel */}
      {mediaItems.length > 0 && (
        <div className="relative aspect-square bg-black">
          {/* Locked Content Overlay */}
          {isLocked && !isOwner && (
            <LockedOverlay post={post} onUnlocked={() => setContentUnlocked(true)} />
          )}
          {isVideo(currentMedia) ? (
            <div className="relative w-full h-full">
              <video
                src={getMediaUrl(currentMedia)}
                className="w-full h-full object-contain"
                loop
                autoPlay
                muted={muted}
                playsInline
              />
              <button
                onClick={() => setMuted(!muted)}
                className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-background/50 flex items-center justify-center"
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            </div>
          ) : (
            <img
              src={getMediaUrl(currentMedia)}
              alt=""
              className="w-full h-full object-contain cursor-pointer"
              loading="lazy"
              onClick={(e) => {
                // Retry loading on click if failed
                if (e.target.dataset.failed === 'true') {
                  e.target.dataset.failed = 'false';
                  e.target.src = getMediaUrl(currentMedia);
                }
              }}
              onError={(e) => {
                console.error('[PostCard] Image load error:', currentMedia, '-> Resolved URL:', getMediaUrl(currentMedia));
                e.target.dataset.failed = 'true';
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = FALLBACK_IMAGE;
              }}
            />
          )}

          {/* Carousel Navigation */}
          {hasMultipleMedia && (
            <>
              <button
                onClick={prevMedia}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-background/50 flex items-center justify-center hover:bg-background/70"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextMedia}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-background/50 flex items-center justify-center hover:bg-background/70"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              
              {/* Dots indicator */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
                {mediaItems.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentMediaIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentMediaIndex ? 'bg-white' : 'bg-muted/80'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Link Embed */}
      {post.link_embed && (
        <div className="px-4 pb-2">
          <LinkEmbedCard 
            embed={post.link_embed} 
            postId={post.post_id}
            isOwner={isOwner}
            isAdmin={isAdmin}
          />
        </div>
      )}

      {/* Locked overlay for text-only posts */}
      {isLocked && !isOwner && mediaItems.length === 0 && (
        <div className="relative min-h-[180px]">
          <LockedOverlay post={post} onUnlocked={() => setContentUnlocked(true)} />
        </div>
      )}

      {/* Actions */}
      <div className="p-4">
        <div className="flex flex-col gap-1.5 mb-3" data-testid="post-action-buttons">
          {/* Row 1: Primary Actions */}
          <div className="flex items-center gap-3">
            <button 
              onClick={handleLike}
              className="flex items-center gap-1 hover:opacity-70 transition-opacity"
            >
              <Heart className={`w-5 h-5 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
            </button>
            {canComment && (
              <Link to={`/post/${post.post_id}`} className="hover:opacity-70 transition-opacity">
                <MessageCircle className="w-5 h-5" />
              </Link>
            )}
            <button
              onClick={handleRepost}
              className="flex items-center gap-1 hover:opacity-70 transition-opacity"
              data-testid={`repost-btn-${post.post_id}`}
            >
              <Repeat2 className={`w-5 h-5 ${reposted ? 'text-green-500' : ''}`} />
            </button>
            <button onClick={handleSave} className="hover:opacity-70 transition-opacity">
              <Bookmark className={`w-5 h-5 ${saved ? 'fill-white' : ''}`} />
            </button>
          </div>
          {/* Row 2: Share, Promote & Monetize */}
          <div className="flex items-center gap-2">
            <ShareIconButton
              url={`/post/${post.post_id}`}
              title={`Post by @${post.author_handle}`}
              description={post.content?.substring(0, 100)}
              className="w-4 h-4"
            />
            <ShareToSocialButton contentType="post" contentId={post.post_id} contentTitle={post.content?.substring(0, 60)} caption={post.content?.substring(0, 200)} />
            {isOwner && <PromoteButton contentType="post" contentId={post.post_id} contentTitle={post.content?.substring(0, 60)} />}
            <MonetizeMenu post={post} isOwner={isOwner} />
          </div>
        </div>

        {/* Like & repost count */}
        {(likeCount > 0 || repostCount > 0 || post.tips_count > 0) && (
          <div className="flex items-center gap-3 mb-2">
            {likeCount > 0 && <p className="font-medium text-sm">{likeCount.toLocaleString()} likes</p>}
            {repostCount > 0 && <p className="text-sm text-muted-foreground">{repostCount.toLocaleString()} reposts</p>}
            <TipBadge tipsCount={post.tips_count} tipsTotal={post.tips_total_usd} />
          </div>
        )}

        {/* Caption */}
        {isEditing ? (
          <div className="mb-2" data-testid="edit-post-form">
            <textarea
              className="w-full bg-muted/30 border border-border rounded-lg p-2 text-sm resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={3}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              autoFocus
              data-testid="edit-post-textarea"
            />
            <div className="flex items-center gap-2 mt-2">
              <Button size="sm" onClick={handleEditPost} data-testid="save-edit-btn">
                <Check className="w-3 h-3 mr-1" /> Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setIsEditing(false); setEditContent(postContent); }} data-testid="cancel-edit-btn">
                <X className="w-3 h-3 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        ) : postContent ? (
          <p className="text-sm">
            <Link to={getProfileUrl({ handle: post.author_handle, user_id: post.user_id || post.artist_id })} className="font-medium hover:underline">
              {getDisplayHandle({ handle: post.author_handle, name: post.author_name })}
            </Link>{' '}
            <RichText text={postContent} />
            {isEdited && <span className="text-xs text-muted-foreground ml-1">(edited)</span>}
          </p>
        ) : null}

        {/* Comments preview */}
        {post.comment_count > 0 && (
          <Link 
            to={`/post/${post.post_id}`} 
            className="text-sm text-muted-foreground mt-2 block hover:underline"
          >
            View all {post.comment_count} comments
          </Link>
        )}

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground mt-2">
          {new Date(post.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: post.created_at?.startsWith(new Date().getFullYear()) ? undefined : 'numeric'
          })}
        </p>
      </div>
      
      {/* Modals */}
      <PostInsightsModal 
        open={showInsights} 
        onOpenChange={setShowInsights} 
        postId={post.post_id}
        postPreview={post.content}
      />
      <BoostPostModal 
        open={showBoost} 
        onOpenChange={setShowBoost} 
        postId={post.post_id}
        onBoostSuccess={() => {
          // Optionally refresh the post or show boosted badge
        }}
      />
    </Card>
  );
}

export default PostCard;
